import { RegionShared } from "@1games-ru/region-shared";
import { EstateComplexLevel } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import rpc from "@1games-ru/rage-rpc";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider.ts";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import toast from "@/domains/toasts/helpers/toast.ts";

export default () => {
  const { t } = useTranslation("estate");
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState(-1);
  const [estateComplexSectionId, setEstateComplexSectionId] = useState<number | null>(null);
  const [level, setLevel] = useState<EstateComplexLevel | null>(null);
  const [estates, setEstates] = useState<{ estateId: number; room: number }[]>([]);

  const getEstateIdByRoom = useCallback(
    async (room: number): Promise<number | null> => {
      const response = await EstateMpProvider.getComplexSectionGuest(estateComplexSectionId, room);

      if (response instanceof RpcMpProviderFailedResponse) {
        console.error("getComplexSectionGuest", response);
        return null;
      }

      return response.data.id;
    },
    [estateComplexSectionId],
  );

  const transit = useCallback(
    async (room: number): Promise<void> => {
      try {
        const estate = estates.find((e) => e.room === room);

        const estateId = estate ? estate.estateId : await getEstateIdByRoom(room);

        if (estateId === null) {
          toast.error(t("estate.errors.estateNotFound"));
          setStatus(0);
          return;
        }

        const response = await EstateMpProvider.livingTransit(estateId);

        if (response instanceof RpcMpProviderFailedResponse) {
          toast.error(t(`estate.errors.${response.error.message}`));
          setStatus(0);
          return;
        }

        setStatus(1);
      } catch (e) {
        console.error("Error during transit:", e);
        toast.error(t("estate.errors.transitFailed"));
        setStatus(0);
      } finally {
        setTimeout(() => {
          setStatus(-1);
        }, 500);
      }
    },
    [estates, getEstateIdByRoom, t],
  );

  const loadEstates = useCallback(
    async (sectionId: number) => {
      const response = await EstateMpProvider.getComplexSectionRelated(sectionId);

      if (response instanceof RpcMpProviderFailedResponse) {
        toast.error(t(`estate.errors.${response.error.message}`));
        return;
      }

      setEstates(
        response.data.items.map((item) => ({
          estateId: item.estateId,
          room: item.room,
        })),
      );
    },
    [t],
  );

  useLayoutEffect(() => {
    rpc.register(
      RegionShared.Cef.Events.Estate.EstateComplexIntercomShow,
      async (
        state: typeof RegionShared.Cef.Requests.EstateComplexIntercomShowRequest,
      ): Promise<void> => {
        console.log("PENES SHOW", { state });

        setShow(true);
        setLevel(state.level);
        setEstateComplexSectionId(state.estateComplexSectionId);

        setTimeout(async () => {
          await loadEstates(state.estateComplexSectionId);
        }, 100);
      },
    );

    rpc.register(
      RegionShared.Cef.Events.Estate.EstateComplexIntercomHide,
      async (): Promise<void> => {
        setShow(false);
        setEstateComplexSectionId(null);
      },
    );

    return () => {
      rpc.unregister(RegionShared.Cef.Events.Estate.EstateComplexIntercomShow);
      rpc.unregister(RegionShared.Cef.Events.Estate.EstateComplexIntercomHide);
    };
  }, [loadEstates, setEstateComplexSectionId]);

  return {
    show,
    status,
    level,
    estates,
    transit,
  };
};
